import { Button, ButtonVariant, Drawer, Spinner, Text } from "@merge-api/merge-javascript-shared";
import { Plus } from "lucide-react";
import { useState } from "react";
import { BlueprintVersionPublishState } from "../../../../models/Blueprints";
import CommonModelDiffRunTab from "../../../integrations/publish-requests/IntegrationStagedVersions/CommonModelDiffRuns/CommonModelDiffRunTab";
import { ViewExecutionLogsHandler } from "../../../integrations/publish-requests/IntegrationStagedVersions/CommonModelDiffRuns/entities";
import useBlueprintContext from "../../context/useBlueprintContext";
import {
  BlueprintAdminEnvironment,
  getCurrentBlueprintAdminEnvironment,
} from "../../utils/BlueprintVersionEnvironmentLinks";

const eligibleCandidateStates: BlueprintVersionPublishState[] = [
  BlueprintVersionPublishState.Draft,
  BlueprintVersionPublishState.Unpublished,
  BlueprintVersionPublishState.Staged,
];

const allowedEnvironments: BlueprintAdminEnvironment[] = [
  BlueprintAdminEnvironment.Develop,
  BlueprintAdminEnvironment.Local,
  BlueprintAdminEnvironment.MuDevelop,
  BlueprintAdminEnvironment.Staging,
  BlueprintAdminEnvironment.Production,
];

export const canShowBlueprintCommonModelDiffsAction = (
  publishState: BlueprintVersionPublishState
) => {
  const currentEnvironment = getCurrentBlueprintAdminEnvironment();
  return (
    !!currentEnvironment &&
    allowedEnvironments.includes(currentEnvironment) &&
    eligibleCandidateStates.includes(publishState)
  );
};

interface BlueprintCommonModelDiffsDrawerProps {
  open: boolean;
  onClose: () => void;
  onViewExecutionLogs?: ViewExecutionLogsHandler;
}

const BlueprintCommonModelDiffsDrawer = ({
  open,
  onClose,
  onViewExecutionLogs,
}: BlueprintCommonModelDiffsDrawerProps) => {
  const { blueprint, blueprintVersions, doesBlueprintHaveUnsavedChanges } = useBlueprintContext();
  const [isAddDiffRunModalOpen, setIsAddDiffRunModalOpen] = useState(false);
  const hasPublishedBlueprint = blueprintVersions.some(
    (version) => version.publish_state === BlueprintVersionPublishState.Published
  );
  const isLoadingVersions = blueprintVersions.length === 0;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Common Model diffs"
      exitPrefix={
        <Button
          disabled={isLoadingVersions || doesBlueprintHaveUnsavedChanges || !hasPublishedBlueprint}
          leftIcon={<Plus size={16} />}
          onClick={() => setIsAddDiffRunModalOpen(true)}
          size="md"
          variant={ButtonVariant.SecondaryGray}
        >
          Common Model Diff run
        </Button>
      }
      anchor="right"
    >
      {isLoadingVersions ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="pb-5">
          {doesBlueprintHaveUnsavedChanges && (
            <Text as="div" className="mb-4 text-gray-40" variant="md">
              Save or discard unsaved changes before running a Common Model diff. Diff runs use the
              saved blueprint version.
            </Text>
          )}
          <CommonModelDiffRunTab
            addDiffRunDisabled={doesBlueprintHaveUnsavedChanges}
            blueprintCategory={blueprint.category}
            blueprintId={blueprint.id}
            blueprintVersionId={blueprint.version.id}
            candidateBlueprintVersionId={blueprint.version.id}
            candidateTitle={
              blueprint.version.publish_state === BlueprintVersionPublishState.Staged
                ? "Staged"
                : "Candidate"
            }
            className="pb-5"
            hasPublishedBlueprint={hasPublishedBlueprint}
            hideAddDiffRunButton
            isAddDiffRunModalOpen={isAddDiffRunModalOpen}
            integrationID={blueprint.integration.id}
            onAddDiffRunModalClose={() => setIsAddDiffRunModalOpen(false)}
            showEmptyState
            onViewExecutionLogs={onViewExecutionLogs}
          />
        </div>
      )}
    </Drawer>
  );
};

export default BlueprintCommonModelDiffsDrawer;
